import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Collapse,
  Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Inventory2 as StockIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import productService from '../../../services/productService';
import PriceHistoryList from './PriceHistoryList';
import PriceHistoryChart from './PriceHistoryChart';
import ProductStockMovements from './ProductStockMovements';
import ImageUploader from '../../common/ImageUploader';
import StockAdjustmentDialog from '../stock-movements/StockAdjustmentDialog';
import ProductTransferDialog from '../stock-movements/ProductTransferDialog';
import { useCanUpdate } from '../../common/activeToggle';
import { useCurrency } from '../../../hooks/useCurrency';

// Component to show expandable row with price history
const VariantRow = ({ variant, onEdit, onDelete, onAdjustStock }) => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();

  const { format: formatPrice } = useCurrency();

  return (
    <>
      <TableRow>
        <TableCell>
          <IconButton
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{variant.shortName || variant.name}</TableCell>
        <TableCell>{variant.sku}</TableCell>
        <TableCell>
          {variant.attributes?.map(attr => (
            <Chip
              key={attr.attributeId}
              label={`${attr.attributeName}: ${attr.displayValue}`}
              size="small"
              sx={{ mr: 0.5, mb: 0.5 }}
            />
          ))}
        </TableCell>
        <TableCell>{formatPrice(variant.price)}</TableCell>
        <TableCell>
          {variant.stockQuantity} ({variant.availableQuantity} available)
        </TableCell>
        <TableCell>
          <Chip
            label={variant.isActive ? t('common.active') : t('common.inactive')}
            color={variant.isActive ? 'success' : 'default'}
            size="small"
          />
        </TableCell>
        <TableCell>
          {onAdjustStock && (
            <Tooltip title={t('stockMovements.adjustStock')}>
              <IconButton size="small" color="info" onClick={() => onAdjustStock(variant)}>
                <StockIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <IconButton size="small" onClick={() => onEdit(variant)}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => onDelete(variant.id)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <Typography variant="h6" gutterBottom>
                Price History for {variant.name}
              </Typography>
              <PriceHistoryList variantId={variant.id} />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const ProductDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const { format: formatPrice } = useCurrency();
  const [product, setProduct] = useState(null);
  const [variants, setVariants] = useState([]);
  const canAdjustStock = useCanUpdate('stock.update');
  const [stockVariant, setStockVariant] = useState(null);
  const [transferOpen, setTransferOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    fetchProductDetails();
  }, [id]);

  const fetchProductDetails = async () => {
    setLoading(true);
    try {
      const productData = await productService.getProductById(id);
      const variantsData = await productService.getProductVariants(id);
      
      setProduct(productData);
      setVariants(variantsData);
    } catch (error) {
      toast.error(t('products.fetchError'));
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleEditVariant = (variant) => {
    navigate(`/products/${id}/variants/edit/${variant.id}`);
  };

  const handleDeleteVariant = async (variantId) => {
    if (window.confirm(t('common.confirmDelete'))) {
      try {
        await productService.deleteProductVariant(variantId);
        toast.success(t('variants.deleteSuccess'));
        fetchProductDetails();
      } catch (error) {
        toast.error(t('common.deleteError'));
      }
    }
  };

  if (loading || !product) {
    return <Box sx={{ p: 3 }}>{t('common.loading')}</Box>;
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">{product.name}</Typography>
        <Box>
          <Button
            variant="outlined"
            onClick={() => navigate('/products')}
            sx={{ mr: 2 }}
          >
            {t('common.back')}
          </Button>
          {canAdjustStock && (
            <Button
              variant="outlined"
              startIcon={<StockIcon />}
              onClick={() => setTransferOpen(true)}
              sx={{ mr: 2 }}
            >
              {t('stockMovements.transferProduct')}
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/products/edit/${id}`)}
          >
            {t('products.editProduct')}
          </Button>
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label={t('common.details')} />
          <Tab label={t('products.variants') + ' & ' + t('products.priceHistory')} />
          <Tab label={t('nav.stockMovements')} />
        </Tabs>
      </Box>

      {/* Tab 0: Product Details */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          {/* Left column: info */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>{t('products.viewProduct')}</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">{t('products.sku')}</Typography>
                    <Typography variant="body1">{product.sku}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">{t('products.basePrice')}</Typography>
                    <Typography variant="body1">
                      {formatPrice(product.basePrice)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>{t('products.categories')}</Typography>
                    {product.categories?.length > 0 ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {/* Primary category first and filled; the others outlined */}
                        {[...product.categories]
                          .sort((a, b) => Number(Boolean(b.isPrimary)) - Number(Boolean(a.isPrimary)))
                          .map((c) => (
                            <Chip
                              key={c.id}
                              label={c.name}
                              size="small"
                              color={c.isPrimary ? 'primary' : 'default'}
                              variant={c.isPrimary ? 'filled' : 'outlined'}
                            />
                          ))}
                      </Box>
                    ) : (
                      <Typography variant="body1">{t('common.noData')}</Typography>
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">{t('common.description')}</Typography>
                    <Typography variant="body1">{product.description || t('common.noData')}</Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Gallery */}
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Gallery</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {product.galleryImages?.map((url, idx) => (
                    <Box key={url} sx={{ position: 'relative' }}>
                      <img
                        src={url}
                        alt={`gallery-${idx}`}
                        style={{
                          width: 120,
                          height: 120,
                          objectFit: 'cover',
                          borderRadius: 8,
                          border: '1px solid #e0e0e0',
                        }}
                      />
                      <IconButton
                        size="small"
                        color="error"
                        sx={{
                          position: 'absolute',
                          top: 2,
                          right: 2,
                          bgcolor: 'rgba(255,255,255,0.85)',
                          '&:hover': { bgcolor: 'white' },
                        }}
                        onClick={async () => {
                          try {
                            await productService.deleteGalleryImage(product.id, url);
                            setProduct((prev) => ({
                              ...prev,
                              galleryImages: prev.galleryImages.filter((u) => u !== url),
                            }));
                            toast.success(t('products_ext.galleryImageRemoved'));
                          } catch {
                            toast.error(t('products_ext.galleryImageRemoveError'));
                          }
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}

                  {/* Add gallery image — max 2 */}
                  {(product.galleryImages?.length ?? 0) < 2 && (
                    <ImageUploader
                      currentUrl={null}
                      onUpload={async (file) => {
                        const { url } = await productService.uploadGalleryImage(product.id, file);
                        setProduct((prev) => ({
                          ...prev,
                          galleryImages: [...(prev.galleryImages || []), url],
                        }));
                        toast.success(t('products_ext.galleryImageAdded'));
                      }}
                      size={120}
                    />
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Right column: status + main image */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>{t('common.status')}</Typography>
                <Chip
                  label={product.isActive ? t('common.active') : t('common.inactive')}
                  color={product.isActive ? 'success' : 'default'}
                  sx={{ mb: 1 }}
                />
                <Box sx={{ mb: 2 }}>
                  <Chip
                    label={product.sellOnline === false ? t('sellOnline.offline') : t('sellOnline.online')}
                    color={product.sellOnline === false ? 'default' : 'info'}
                    variant="outlined"
                    size="small"
                  />
                </Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t('common.quantity')}
                </Typography>
                <Typography variant="h5" sx={{ mb: 3 }}>{product.totalStock || 0}</Typography>

                <Typography variant="subtitle2" gutterBottom>Main Image</Typography>
                <ImageUploader
                  currentUrl={product.mainImageUrl}
                  onUpload={async (file) => {
                    const { url } = await productService.uploadMainImage(product.id, file);
                    setProduct((prev) => ({ ...prev, mainImageUrl: url }));
                    toast.success(t('products_ext.mainImageUpdated'));
                  }}
                  onDelete={async () => {
                    await productService.deleteMainImage(product.id);
                    setProduct((prev) => ({ ...prev, mainImageUrl: null }));
                    toast.success(t('products_ext.mainImageRemoved'));
                  }}
                  size={200}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tab 1: Variants with Price History */}
      {activeTab === 1 && (
        <Box>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {t('products.variants')} ({variants.length})
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate(`/products/${id}/variants/new`)}
            >
              {t('products.addVariant')}
            </Button>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width={50} />
                  <TableCell>{t('common.name')}</TableCell>
                  <TableCell>{t('products.sku')}</TableCell>
                  <TableCell>{t('variants.attributes')}</TableCell>
                  <TableCell>{t('common.price')}</TableCell>
                  <TableCell>{t('variants.stockQuantity')}</TableCell>
                  <TableCell>{t('common.status')}</TableCell>
                  <TableCell>{t('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {variants.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No variants found. Create your first variant to get started.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  variants.map((variant) => (
                    <VariantRow
                      key={variant.id}
                      variant={variant}
                      onEdit={handleEditVariant}
                      onDelete={handleDeleteVariant}
                      onAdjustStock={canAdjustStock ? setStockVariant : undefined}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Tab 2: Stock Movements */}
      {activeTab === 2 && (
        <Box>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t('nav.stockMovements')}
          </Typography>
          <ProductStockMovements productId={id} />
        </Box>
      )}

      <ProductTransferDialog
        open={transferOpen}
        product={product}
        variants={variants}
        onClose={() => setTransferOpen(false)}
        onSaved={fetchProductDetails}
      />

      <StockAdjustmentDialog
        open={Boolean(stockVariant)}
        variant={stockVariant}
        storeId={product?.storeId}
        onClose={() => setStockVariant(null)}
        onSaved={fetchProductDetails}
      />
    </Box>
  );
};

export default ProductDetails;